import { useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DuotoneIcon, Icon } from "@/components/ui/icon";
import { SidebarNavButton } from "./SidebarNavButton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { KiteMark } from "@/components/ui/landing-primitives";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const navItems = [
  { to: "/dashboard",          icon: "home",         label: "Overview" },
  { to: "/know-your-competitors", icon: "radar",   label: "Know your competitors" },
  { to: "/alerts",             icon: "bell",         label: "Alerts" },
  { to: "/watchlist",          icon: "target",       label: "Watchlist" },
  { to: "/intelligence",       icon: "chart",        label: "Intelligence" },
  { to: "/scrape",             icon: "wand",         label: "Scrape" },
  { to: "/chat",               icon: "chat-square",  label: "Chat" },
  { to: "/settings",           icon: "settings",     label: "Settings" },
];

// Platform admins only (ADMIN_EMAILS on the worker)
const adminNavItems = [
  { to: "/admin/social", icon: "share", label: "Social publishing" },
];

function useNavItems() {
  const { user } = useAuth();
  return user?.isAdmin ? [...navItems, ...adminNavItems] : navItems;
}

// Routes that auto-collapse the sidebar
const AUTO_COLLAPSE_ROUTES = ["/chat"];

export function WorkspaceLogo({ src, name, size = "md" }: { src: string | null; name?: string; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "h-6 w-6" : "h-7 w-7";
  const imgDim = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const textSize = "text-[11px]";

  return (
    <div className={`${dim} shrink-0 rounded-lg overflow-hidden border border-border bg-muted flex items-center justify-center`}>
      {src ? (
        <img src={src} alt={name} className={`${imgDim} object-contain`}
          onError={(e) => {
            const el = e.target as HTMLImageElement;
            el.style.display = "none";
            el.parentElement!.innerHTML = `<span class="font-mono ${textSize} font-bold uppercase">${name?.[0] ?? "W"}</span>`;
          }} />
      ) : (
        <span className={`font-mono ${textSize} font-bold uppercase`}>{name?.[0] ?? "W"}</span>
      )}
    </div>
  );
}

// ─── Mobile bottom tab bar ────────────────────────────────────────────────────

// Show 5 items in the bottom bar; the rest go in the drawer
const BOTTOM_NAV = navItems.slice(0, 5);

function MobileBottomBar({ onMenuOpen }: { onMenuOpen: () => void }) {
  const location = useLocation();
  const { workspace } = useAuth();

  const logoUrl = workspace?.logoUrl
    ?? (workspace?.domain ? `https://www.google.com/s2/favicons?domain=${workspace.domain}&sz=64` : null)
    ?? null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center border-t border-border bg-card md:hidden">
      <KiteMark className="top-[-6px] left-1/2 -translate-x-1/2" />
      {BOTTOM_NAV.map(({ to, icon, label }) => {
        const isActive = location.pathname.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            className={cn(
              "flex flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors",
              isActive ? "text-foreground" : "text-muted-foreground"
            )}
          >
            <span className={cn(
              "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
              isActive ? "bg-foreground text-background" : ""
            )}>
              <DuotoneIcon name={icon} size={18} />
            </span>
            <span className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</span>
          </Link>
        );
      })}

      {/* Menu button — opens drawer */}
      <button
        onClick={onMenuOpen}
        className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-muted-foreground transition-colors"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg">
          {logoUrl ? (
            <img src={logoUrl} alt={workspace?.name} className="h-5 w-5 rounded object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          ) : (
            <DuotoneIcon name="menu" size={16} />
          )}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-widest leading-none">More</span>
      </button>
    </nav>
  );
}

// ─── Mobile drawer (sheet) ────────────────────────────────────────────────────

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, workspace, workspaces, logout, switchWorkspace } = useAuth();
  const items = useNavItems();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  // Close drawer on navigation
  useEffect(() => { onClose(); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const logoUrl = workspace?.logoUrl
    ?? (workspace?.domain ? `https://www.google.com/s2/favicons?domain=${workspace.domain}&sz=64` : null)
    ?? (user?.email ? `https://www.google.com/s2/favicons?domain=${user.email.split("@")[1]}&sz=64` : null);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="left" className="w-72 p-0 flex flex-col">
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation</SheetTitle>
        </SheetHeader>

        {/* Workspace header */}
        <div className="relative flex h-14 items-center gap-2.5 border-b border-border px-4">
          <KiteMark className="bottom-[-6px] right-[-6px]" />
          <WorkspaceLogo src={logoUrl} name={workspace?.name} />
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-widest truncate">
              {workspace?.name ?? "Workspace"}
            </p>
            <p className="font-sans text-[11px] text-muted-foreground capitalize">{workspace?.plan} plan</p>
          </div>
        </div>

        {/* All nav items */}
        <ScrollArea className="flex-1">
            <nav className="space-y-0.5 px-2 py-3">
              {items.map(({ to, icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors",
                    "font-mono text-[11px] uppercase tracking-widest",
                    location.pathname.startsWith(to)
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <DuotoneIcon name={icon} size={16} className="shrink-0" />
                  {label}
                </Link>
              ))}
            </nav>

          {/* Other workspaces */}
          {workspaces.filter(w => w.id !== workspace?.id).length > 0 && (
            <>
              <Separator className="my-1" />
              <div className="px-4 py-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Switch workspace</p>
                {workspaces.filter(w => w.id !== workspace?.id).map(w => (
                  <button
                    key={w.id}
                    onClick={() => switchWorkspace(w.id)}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-accent transition-colors"
                  >
                    <WorkspaceLogo
                      src={w.logoUrl ?? (w.domain ? `https://www.google.com/s2/favicons?domain=${w.domain}&sz=32` : null)}
                      name={w.name}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1 text-left">
                      <p className="font-mono text-[11px] uppercase tracking-widest truncate">{w.name}</p>
                      {w.domain && <p className="font-mono text-[10px] text-muted-foreground truncate">{w.domain}</p>}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </ScrollArea>

        {/* Footer — theme + user */}
        <div className="relative border-t border-border p-3 space-y-1">
          <KiteMark className="top-[-6px] right-[-6px]" />
          <button
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-accent transition-colors text-muted-foreground"
          >
            <DuotoneIcon name={resolvedTheme === "dark" ? "sun" : "moon"} size={14} className="shrink-0" />
            <span className="font-mono text-[11px] uppercase tracking-widest">
              {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
            </span>
          </button>

          <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
            <div className="h-6 w-6 shrink-0 rounded-full border border-border overflow-hidden bg-muted flex items-center justify-center">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                <span className="font-mono text-[11px] uppercase">{user?.name?.[0] ?? "U"}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate font-sans text-xs font-medium">{user?.name}</p>
              <p className="truncate font-sans text-[11px] text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleLogout}>
              <DuotoneIcon name="logout" size={13} />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Desktop sidebar ──────────────────────────────────────────────────────────

export function Sidebar() {
  const { user, workspace, workspaces, logout, switchWorkspace } = useAuth();
  const items = useNavItems();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Auto-collapse on certain routes, manual toggle otherwise
  const shouldAutoCollapse = AUTO_COLLAPSE_ROUTES.some(r => location.pathname.startsWith(r));
  const [collapsed, setCollapsed] = useState(shouldAutoCollapse);

  useEffect(() => {
    if (shouldAutoCollapse) setCollapsed(true);
  }, [shouldAutoCollapse]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const logoUrl = workspace?.logoUrl
    ?? (workspace?.domain ? `https://www.google.com/s2/favicons?domain=${workspace.domain}&sz=64` : null)
    ?? (user?.email ? `https://www.google.com/s2/favicons?domain=${user.email.split("@")[1]}&sz=64` : null);

  return (
    <TooltipProvider delayDuration={0}>
      {/* ── Mobile bottom bar + drawer ── */}
      <MobileBottomBar onMenuOpen={() => setMobileDrawerOpen(true)} />
      <MobileDrawer open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} />

      {/* ── Desktop sidebar ── */}
      <aside className={cn(
        "hidden md:flex h-screen flex-col border-r border-border bg-card transition-all duration-200",
        collapsed ? "w-14" : "w-60"
      )}>

        {/* Header — logo + toggle */}
        <div className={cn(
          "relative flex h-14 items-center border-b border-border",
          collapsed ? "justify-center px-0" : "px-3 gap-2"
        )}>
          <KiteMark className="bottom-[-6px] right-[-6px]" />
          {!collapsed && workspace && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex flex-1 items-center gap-2.5 hover:bg-accent rounded-lg px-1 py-1 transition-colors min-w-0">
                  <WorkspaceLogo src={logoUrl} name={workspace?.name} />
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-widest truncate">
                      {workspace?.name ?? "Workspace"}
                    </p>
                  </div>
                  <Icon name="arrow-down-01" size={14} className="text-muted-foreground shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="start" sideOffset={4} className="w-64">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-2">
                    <WorkspaceLogo src={logoUrl} name={workspace?.name} size="sm" />
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] uppercase tracking-widest truncate">{workspace?.name}</p>
                      <p className="font-sans text-xs text-muted-foreground tracking-normal">{workspace?.plan} plan</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {workspaces.filter(w => w.id !== workspace?.id).length > 0 && (
                  <>
                    <div className="px-2 py-1">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Switch workspace</p>
                    </div>
                    {workspaces.filter(w => w.id !== workspace?.id).map(w => (
                      <DropdownMenuItem
                        key={w.id}
                        onClick={() => switchWorkspace(w.id)}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <WorkspaceLogo
                          src={w.logoUrl ?? (w.domain ? `https://www.google.com/s2/favicons?domain=${w.domain}&sz=32` : null)}
                          name={w.name}
                          size="sm"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-[11px] uppercase tracking-widest truncate">{w.name}</p>
                          {w.domain && <p className="font-mono text-[11px] text-muted-foreground truncate">{w.domain}</p>}
                        </div>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}

                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=workspace" className="flex items-center gap-2">
                    <DuotoneIcon name="settings" size={13} />Workspace settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=team" className="flex items-center gap-2">
                    <DuotoneIcon name="users" size={13} />Invite team members
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=workspace&action=create" className="flex items-center gap-2">
                    <DuotoneIcon name="plus" size={13} />Create new workspace
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {collapsed && workspace && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCollapsed(false)}
                  className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-accent transition-colors"
                >
                  <WorkspaceLogo src={logoUrl} name={workspace?.name} size="sm" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{workspace?.name ?? "Workspace"} — click to expand</TooltipContent>
            </Tooltip>
          )}

          {!collapsed && workspace && (
            <button
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
              className="h-7 w-7 shrink-0 flex items-center justify-center rounded-lg hover:bg-accent transition-colors text-muted-foreground"
            >
              <Icon name="panel-left-close" size={16} />
            </button>
          )}
        </div>

        {/* Nav */}
        <ScrollArea className="flex-1">
          <div className="sidebar-panel h-full">
              <nav className={cn("py-3", collapsed ? "flex flex-col" : "space-y-0.5 px-2")}>
                {items.map(({ to, icon, label }) => (
                  <SidebarNavButton
                    key={to}
                    to={to}
                    icon={icon}
                    label={label}
                    collapsed={collapsed}
                  />
                ))}
              </nav>
          </div>
        </ScrollArea>

        {/* User */}
        <Separator />
        <div className={cn("relative p-2", collapsed ? "flex flex-col items-center gap-1" : "p-3")}>
          <KiteMark className="top-[-6px] right-[-6px]" />
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                  className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-accent transition-colors text-muted-foreground"
                >
                  <DuotoneIcon name={resolvedTheme === "dark" ? "sun" : "moon"} size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{resolvedTheme === "dark" ? "Light mode" : "Dark mode"}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors text-muted-foreground mb-1"
            >
              <DuotoneIcon name={resolvedTheme === "dark" ? "sun" : "moon"} size={13} className="shrink-0" />
              <span className="font-mono text-[11px] uppercase tracking-widest">
                {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
              </span>
            </button>
          )}

          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className="h-8 w-8 flex items-center justify-center rounded-full border border-border overflow-hidden bg-muted hover:bg-accent transition-colors"
                >
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="font-mono text-[11px] uppercase">{user?.name?.[0] ?? "U"}</span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{user?.name} · Sign out</TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
              <div className="h-6 w-6 shrink-0 rounded-full border border-border overflow-hidden bg-muted flex items-center justify-center">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.name} className="h-full w-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                ) : (
                  <span className="font-mono text-[11px] uppercase">{user?.name?.[0] ?? "U"}</span>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="truncate font-sans text-xs font-medium">{user?.name}</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleLogout}>
                    <DuotoneIcon name="logout" size={12} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Sign out</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
